import * as Yup from 'yup'
import dayjs from 'dayjs'

import { AdvertiserConfig, BudgetCapping, CostType, DailyBudgetType, Network } from '../../__generated__/graphql'
import { urlRegexWithTrackingParams } from '../../utils/UrlRegex'

export const networkSchema = Yup.object().shape({
  redirectUrl: Yup.string()
    .matches(urlRegexWithTrackingParams, 'commons.invalidUrl')
    .min(3)
    .required('commons.required'),
  channels: Yup.array().of(Yup.string()),
  network: Yup.string().default(Network.Media).required('commons.required'),
})

export type BudgetAndDateSchemaOptions = {
  adgroupDatesRequired?: boolean
}

const isPresentDateValue = (value: unknown): boolean => value != null && value !== '' && dayjs(value).isValid()

const requiredAdgroupDateSchema = (): Yup.MixedSchema =>
  Yup.mixed().test('required', 'commons.required', (value) => isPresentDateValue(value))

const endDateAfterStartDateTest = function (this: Yup.TestContext, value: unknown): boolean {
  const { startDate } = this.parent as { startDate?: unknown }
  if (!isPresentDateValue(value) || !isPresentDateValue(startDate)) return true
  return !dayjs(value).isBefore(dayjs(startDate), 'day')
}

export const budgetAndDateSchema = (
  config: AdvertiserConfig['adGroup'],
  options: BudgetAndDateSchemaOptions = {}
): Yup.ObjectSchema<any> => {
  const { adgroupDatesRequired = false } = options

  const startDateSchema = adgroupDatesRequired
    ? requiredAdgroupDateSchema()
    : Yup.date().when('budgetCappingType', {
        is: BudgetCapping.Period,
        then: (schema) => schema.required('commons.required'),
        otherwise: (schema) => schema.nullable(),
      })

  const endDateSchema = adgroupDatesRequired
    ? requiredAdgroupDateSchema().test(
        'end-after-start',
        "End date can't be before start date",
        endDateAfterStartDateTest
      )
    : Yup.date()
        .min(Yup.ref('startDate'), "End date can't be before start date")
        .when(['dailyBudgetType', 'budgetCappingType'], {
          is: (dailyBudgetType: DailyBudgetType, budgetCappingType: BudgetCapping) =>
            dailyBudgetType === DailyBudgetType.Auto && budgetCappingType === BudgetCapping.Overall,
          then: (schema) => schema.required('commons.required'),
          otherwise: (schema) => schema.nullable(),
        })

  return Yup.object().shape({
    costValue: Yup.number()
      .when('costType', {
        is: CostType.Cpc,
        then: (schema) => schema.min(config.min.cpc).max(config.max.cpc),
      })
      .when('costType', {
        is: CostType.Cpl,
        then: (schema) => schema.min(config.min.cpl).max(config.max.cpl),
      })
      .when('costType', {
        is: CostType.Cpo,
        then: (schema) => schema.min(config.min.cpo).max(config.max.cpo),
      })
      .when('costType', {
        is: CostType.Cpm,
        then: (schema) => schema.min(config.min.cpm).max(config.max.cpm),
      })
      .required('commons.required'),
    costType: Yup.string().required('commons.required'),
    dailyBudget: Yup.number().when('dailyBudgetType', {
      is: DailyBudgetType.Fixed,
      then: (schema) => schema.min(config.min.dailyBudget).max(config.max.dailyBudget),
    }),
    dailyBudgetType: Yup.string().required('commons.required'),
    budgetCapping: Yup.number().when('budgetCappingType', {
      is: BudgetCapping.Open,
      then: (schema) => schema,
      otherwise: (schema) =>
        schema.min(config.min.budgetCapping).max(config.max.budgetCapping).required('commons.required'),
    }),
    budgetCappingType: Yup.string()
      .when('dailyBudgetType', {
        is: DailyBudgetType.Auto,
        then: (schema) => schema.notOneOf([BudgetCapping.Open]),
      })
      .required('commons.required'),
    startDate: startDateSchema,
    endDate: endDateSchema,
  })
}
